import React, { useState } from 'react';
import { useKmClient } from '../../../lib/useKmClient.js';
import { useKmQuery } from '../hooks/useKmQuery.js';
import ErrorDetails from '../components/ErrorDetails.jsx';
import ModelliLlmPanel from '../components/ModelliLlmPanel.jsx';

/* Nuova — costruita sul contratto reale confermato nel collaudo
   (Collaudo_KM_TalentGPT_20260907_2.docx, §B.7): GET /admin/kpi?mesi=N
   risponde { metriche: [{ metrica, valore, periodo, calcolato_il }] }.
   Accesso "●" (qualsiasi utente autenticato, non solo admin).

   Risponde all'FRD KM v1.0 §6 (User Metrics & Dashboard), che descrive 4
   dimensioni: Adoption, Completezza, Valore commerciale, Soddisfazione
   consulenti. I codici osservati nel collaudo (MET-A01, MET-A03, MET-C01)
   suggeriscono un prefisso per dimensione (A/C/...), ma NON esiste da
   nessuna parte una legenda che spieghi cosa significa ogni codice — non
   la invento. Raggruppo per prefisso lettera (euristica, dichiarata come
   tale) e mostro il codice grezzo accanto al valore, invece di inventare
   un'etichetta leggibile.

   "Una metrica priva di dati non compare: non rappresentarla con uno
   zero" (nota testuale nel collaudo) — rispettata: nessun placeholder a 0
   per metriche assenti. */

const DIMENSIONE_PREFISSI = {
  A: 'Adoption',
  C: 'Completezza',
  V: 'Valore commerciale',
  S: 'Soddisfazione consulenti',
};

function dimensioneDaCodice(metrica) {
  const match = /^MET-([A-Z])/.exec(metrica || '');
  return match ? (DIMENSIONE_PREFISSI[match[1]] || `Prefisso ${match[1]} (non mappato)`) : 'Non classificata';
}

export default function DashboardPage() {
  const kmClient = useKmClient('km');
  const [mesi, setMesi] = useState(12);

  const { data, status, error, refetch } = useKmQuery(
    () => kmClient.get('/admin/kpi', { query: { mesi } }),
    [mesi]
  );

  const metriche = Array.isArray(data?.metriche) ? data.metriche : [];

  // Raggruppamento per dimensione (euristica sul prefisso codice, non una
  // classificazione confermata dal backend) e poi per codice metrica,
  // tenendo solo il punto più recente per periodo (la risposta include
  // storico, non solo l'ultimo valore).
  const perDimensione = {};
  metriche.forEach((m) => {
    const dim = dimensioneDaCodice(m.metrica);
    perDimensione[dim] = perDimensione[dim] || {};
    const esistente = perDimensione[dim][m.metrica];
    if (!esistente || new Date(m.periodo) > new Date(esistente.periodo)) {
      perDimensione[dim][m.metrica] = m;
    }
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
        <h1 className="section-title" style={{ margin: 0 }}>Dashboard</h1>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
          Ultimi
          <select className="field-select" value={mesi} onChange={(e) => setMesi(Number(e.target.value))} style={{ width: 90 }}>
            {[3, 6, 12, 24, 36].map((n) => <option key={n} value={n}>{n} mesi</option>)}
          </select>
        </label>
      </div>

      <ModelliLlmPanel />

      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginBottom: 'var(--space-5)' }}>
        Raggruppamento per dimensione basato sul prefisso del codice metrica (es. MET-A01 → Adoption) — euristica, non una classificazione confermata dal backend. Nessuna legenda ufficiale dei codici è ancora disponibile.
      </p>

      {status === 'loading' && (
        <div className="card"><div className="skeleton-line" style={{ width: '50%' }} /></div>
      )}

      {status === 'error' && (
        <div className="state-error">
          Non è stato possibile caricare le metriche.
          <div><button type="button" className="btn-link" onClick={refetch}>Riprova</button></div>
          <ErrorDetails error={error} />
        </div>
      )}

      {status === 'success' && metriche.length === 0 && (
        <div className="state-empty">Nessuna metrica disponibile per il periodo selezionato.</div>
      )}

      {status === 'success' && metriche.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          {Object.entries(perDimensione).map(([dimensione, byMetrica]) => (
            <div key={dimensione} className="card">
              <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>{dimensione}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 'var(--space-4)' }}>
                {Object.values(byMetrica).map((m) => (
                  <div key={m.metrica}>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700 }}>{m.valore}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
                      {m.metrica} · {m.periodo}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
